"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { INERT_SLOT } from "../../atoms/inert-slot";
import styles from "./content-ribbon.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/** The ribbon's client half. "use client" is earned by RUNTIME MEASUREMENT and
 *  by nothing else: how far one loop travels is the drawn width of the
 *  wording, which no server can know. The phrases and their dots arrive as
 *  children, already rendered, so none of them crosses the boundary. */

/** The mark the row writes on itself once it has measured a loop worth
 *  travelling. ⚠ MIRRORED ON THREE SIDES — the JSX below, the stylesheet and
 *  the spec — because neither a ref nor a handler crosses a Server Component. */
export const CRAWLING_ROW = '[data-cr-crawling="true"]';

/** The mark the animation is keyed on, written through the ref only once BOTH
 *  custom properties are, so the crawl is never created against the sheet's
 *  zero defaults. Mirrored in the stylesheet and the spec. */
export const ARMED_ATTRIBUTE = "data-cr-armed";
export const ARMED_ROW = `[${ARMED_ATTRIBUTE}="true"]`;

/** The band the row is measured against. ⚠ FOUND BY ITS OWN MARK AND NOT BY
 *  `parentElement`: a rail or an adapter's layout shim slipped between the two
 *  would be measured instead, and a wrapper that hugs its content is never
 *  wider than the row, so the band would never crawl. */
export const BAND = "[data-cr-background]";

/** The pair the measurement writes, read by the keyframe and the animation. */
export const TRAVEL_PROPERTY = "--cr-travel";
export const DURATION_PROPERTY = "--cr-duration";

/** The least slack the width comparison takes: wording a rounding error wider
 *  than the band is no overflow worth a loop. */
export const MEASURE_EPSILON_PX = 0.5;

/** `useLayoutEffect` in the browser and `useEffect` on the server, where the
 *  first warns. The measurement has to land BEFORE the first paint, or the row
 *  paints one frame of a loop with no distance in it. */
const useMeasureEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** A speed judged positive, finite and in range; this row renders alone. */
export type RibbonSpeed = number & { readonly judged: "ribbon-speed" };

interface RibbonTrackProps {
  /** One authored pass — the phrases and their dots, already rendered. */
  children: React.ReactNode;
  /** How fast the row travels, in CSS pixels per second. */
  speed: RibbonSpeed;
}

/** How wide the wording draws: the pass, without the dot that ends it and
 *  without the gap in front of that dot.
 *
 *  ⚠ THE SAME FIGURE WHETHER OR NOT THE ROW IS CRAWLING, which is the whole
 *  point. The sheet draws that last dot only on a row already crawling, so a
 *  width read off the pass's own box is a dot and a gap wider once crawling than
 *  it was at rest — and one band then answers two ways depending on how the
 *  reader got to it. Measuring the ends of the wording leaves out everything
 *  that comes and goes, and follows the tier's own dot and gap on its own. */
function wordingWidth(pass: HTMLElement): number {
  const drawn = Array.from(pass.children);
  const seam = drawn[drawn.length - 1];

  if (seam?.matches(`.${styles.separator}`)) {
    drawn.pop();
  }

  const first = drawn[0]?.getBoundingClientRect();
  const last = drawn[drawn.length - 1]?.getBoundingClientRect();

  /* A pass that ends on no dot at all — one item, or a row built by something
     other than the band: its own box is the whole of the wording. */
  if (!first || !last) {
    return pass.getBoundingClientRect().width;
  }

  /* Both edges of both boxes, because which end carries the leading one is the
     direction's to say: an RTL pass draws its first phrase at the right. */
  return Math.max(first.right, last.right) - Math.min(first.left, last.left);
}

/** The crawl running on the row, if there is one. Found by the name the row's
 *  own style resolves rather than by position, since the sheet's name is
 *  hashed. This assumes the crawl is the row's only animation: a second one
 *  turns the computed name into a list, nothing matches, the carry is skipped
 *  and the restart jumps. jsdom ships no Web Animations API, so there it is
 *  never found. */
function runningCrawl(row: HTMLElement): CSSAnimation | undefined {
  if (typeof row.getAnimations !== "function" || typeof CSSAnimation !== "function") {
    return undefined;
  }

  const { animationName } = getComputedStyle(row);

  const isCrawl = (animation: Animation): animation is CSSAnimation => (
    animation instanceof CSSAnimation && animation.animationName === animationName
  );

  return row.getAnimations().find(isCrawl);
}

/** One loop's length in milliseconds, or nothing with no usable timing. */
function loopLength(crawl: CSSAnimation | undefined): number | undefined {
  const duration = crawl?.effect?.getComputedTiming().duration;

  return typeof duration === "number" && duration > 0 ? duration : undefined;
}

/** How long the crawl has been into its current loop, in milliseconds. */
function loopElapsed(row: HTMLElement): number | undefined {
  const crawl = runningCrawl(row);
  const length = loopLength(crawl);
  const elapsed = crawl?.currentTime;

  if (length === undefined || typeof elapsed !== "number") {
    return undefined;
  }

  return elapsed % length;
}

function RibbonTrackImpl({ children, speed }: RibbonTrackProps) {
  const track = useRef<HTMLDivElement>(null);
  const pass = useRef<HTMLUListElement>(null);
  const copy = useRef<HTMLUListElement>(null);

  /* ⚠ STATE RATHER THAN A REF WRITTEN IN THE EFFECT, though the two custom
     properties beside it are exactly that: this one decides whether the copy
     is RENDERED, so a ref would leave the seam nothing to measure against. */
  const [crawling, setCrawling] = useState(false);

  useMeasureEffect(() => {
    const row = track.current;
    const authored = pass.current;
    const band = row?.closest(BAND);

    if (!row || !authored || !band) {
      return undefined;
    }

    const resolve = () => {
      const wording = wordingWidth(authored);
      const bandWidth = band.getBoundingClientRect().width;

      /* ⚠ FINITE AND POSITIVE, not merely positive: a rect read before layout
         settles arrives as 0, which a bare comparison reads as a row that
         fits. */
      if (!(wording > 0) || !(bandWidth > 0)) {
        return;
      }

      /* THE WORDING AGAINST THE BAND AND NOT THE PASS AGAINST IT: the seam's
         dot and gap are drawn outside the band on every row that crawls, so
         counting them would loop a band the reader can already read in full. */
      if (wording <= bandWidth + MEASURE_EPSILON_PX) {
        row.removeAttribute(ARMED_ATTRIBUTE);
        row.style.removeProperty(TRAVEL_PROPERTY);
        row.style.removeProperty(DURATION_PROPERTY);
        setCrawling(false);
        return;
      }

      setCrawling(true);

      const trailing = copy.current;

      /* Nothing to measure against until the copy has been rendered, which is
         the commit after the line above. The effect runs again with it there. */
      if (!trailing) {
        return;
      }

      const behind = trailing.getBoundingClientRect();

      /* ⚠ A COPY WITH NO BOX IS NOT A COPY AT THE ORIGIN: reduced motion
         takes the copy out of the flow, and its zero rect against an RTL
         pass's own negative left subtracts to a travel that is positive,
         plausible and nothing's distance — which the row then animates on the
         way back out of reduced motion. The observer brings the reading. */
      if (!(behind.width > 0)) {
        return;
      }

      /* ONE LOOP, MEASURED RATHER THAN DERIVED: the distance between the two
         passes' leading edges IS one pass plus the gap that follows it, so
         reading it off the layout means the gap can never be left out. */
      const travel = behind.left - authored.getBoundingClientRect().left;

      /* ⚠ THE MARK STAYS SET AND THE ROW FREEZES, DELIBERATELY: an RTL row
         lays the copy out before the pass, so the width between them is no
         loop. Clearing the mark instead is a render loop — the overflow that
         started the crawl is still true — and all three gates tried on that
         reset have failed. `design-conflicts/content-ribbon.mdx` carries them. */
      if (!(travel > 0)) {
        return;
      }

      const loop = `${travel.toFixed(2)}px`;
      const duration = `${(travel / speed).toFixed(3)}s`;
      const armed = row.hasAttribute(ARMED_ATTRIBUTE);

      /* An unchanged reading leaves a running crawl alone: the observer fires
         on every resize, and there is nothing to restart. */
      if (
        armed &&
        row.style.getPropertyValue(TRAVEL_PROPERTY) === loop &&
        row.style.getPropertyValue(DURATION_PROPERTY) === duration
      ) {
        return;
      }

      /* Read BEFORE the new pair is written, which retimes a running crawl. */
      const elapsed = armed ? loopElapsed(row) : undefined;

      row.style.setProperty(TRAVEL_PROPERTY, loop);
      row.style.setProperty(DURATION_PROPERTY, duration);

      /* ⚠ WEBKIT WORKAROUND: Safari holds a running animation's keyframe `var()`
         at its first value, so a new loop restarts the crawl (mark off, forced
         style pass, mark on). Remove once WebKit re-resolves it. */
      if (armed) {
        row.removeAttribute(ARMED_ATTRIBUTE);
        row.getBoundingClientRect();
      }

      row.setAttribute(ARMED_ATTRIBUTE, "true");

      if (elapsed !== undefined) {
        const restarted = runningCrawl(row);
        const length = loopLength(restarted);

        if (restarted && length !== undefined) {
          // Time, not a fraction: at constant speed the same time into a loop is the same place.
          restarted.currentTime = elapsed % length;
        }
      }
    };

    resolve();

    /* jsdom ships no ResizeObserver, and unguarded this throws in a consumer's
       test rather than in ours. Without one the row keeps the measurement
       above and does not follow a later resize. */
    if (typeof ResizeObserver !== "function") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      resolve();
    });

    /* Three boxes, three changes: the band for a resize, the pass for wording
       a webfont widens, and the copy because `prefers-reduced-motion` hides it
       — ⚠ AND THAT IS WHAT MAKES THE CUT TWO-WAY: with no box to measure, the
       guard above writes nothing, so the band would stay static for good. */
    observer.observe(band);
    observer.observe(authored);

    if (copy.current) {
      observer.observe(copy.current);
    }

    return () => {
      observer.disconnect();
    };

    /* ⚠ `crawling` IS A DEPENDENCY THOUGH NOTHING ABOVE READS IT, and the loop
       has no travel without it: the copy the travel is measured against only
       exists on the render after the flip, so this effect has to run again then
       to reach it. `speed` is here because the duration is derived from it. */
  }, [crawling, speed]);

  return (
    <div className={styles.track} data-cr-crawling={String(crawling)} ref={track}>
      {/* Told it IS a list, because `list-style: none` costs Safari the role
          and the count and boundary go with it (WCAG 1.3.1). */}
      <ul className={styles.pass} ref={pass} role="list">
        {children}
      </ul>

      {crawling && (
        /* The copy that hides the seam, out of the accessibility tree because
           it is the same wording — and needing no `role` for the same reason.
           ⚠ `aria-hidden` IS CARRYING THIS ON ITS OWN: React 18.3.1 spells
           `inert` as an empty-string boolean, which the development build
           validates away and the production build writes. The copy is out of
           the tab order either way, holding nothing focusable. */
        <ul
          aria-hidden="true"
          className={`${styles.pass} ${styles.duplicate}`}
          ref={copy}
          {...INERT_SLOT}
        >
          {children}
        </ul>
      )}
    </div>
  );
}
export const RibbonTrack = withSafeRender(RibbonTrackImpl, "RibbonTrack");

export default RibbonTrack;
